import { DOCUMENT } from '@angular/common';
import {
  Component,
  HostBinding,
  computed,
  effect,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService, AuthUser, PinnedBranch } from '@tvd/data-access/auth';
import {
  SubscriptionStore,
  resolveBillingRoute,
} from '@tvd/data-access/subscription';
import { filter, map, startWith } from 'rxjs';
import { TRIAL_BANNER_CONFIG } from './trial-banner-config';

/**
 * Rendered banner height in CSS pixels. Hardcoded because measuring at
 * runtime (ResizeObserver) would race with layout pass; the banner is a
 * fixed-height strip (text-xs ≈ 16px + py-1 = 8px + 1px border ≈ 25px,
 * rounded up to 28px). Kept in sync with the template classes.
 */
const TRIAL_BANNER_HEIGHT_PX = 28;

@Component({
  selector: 'tvd-trial-banner',

  imports: [RouterLink, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @if (showBanner()) {
      <div class="w-full bg-card border-b border-card-border px-3 py-1 text-xs">
        <div class=" mx-auto flex items-center justify-center text-center">
          <div class="text-secondary">
            <span class="text-primary font-medium">{{
              'trialBanner.trialDaysLeft' | translate: { days: trialDaysLeft() }
            }}</span>
            -
            <a
              class="text-primary underline underline-offset-2"
              [routerLink]="billingRoute()"
              [attr.aria-label]="'Open subscription settings to purchase'"
              >{{ 'trialBanner.purchaseHere' | translate }}</a
            >
          </div>
        </div>
      </div>
    }
  `,
})
export class TrialBannerComponent {
  private readonly subscriptionStore = inject(SubscriptionStore);
  private readonly auth = inject(AuthService);
  private readonly authUser = inject(AuthUser);
  private readonly pinnedBranch = inject(PinnedBranch);
  private readonly router = inject(Router);
  private readonly config = inject(TRIAL_BANNER_CONFIG);
  private readonly doc = inject(DOCUMENT);

  // Publish the banner height as a CSS variable on :root so layout
  // containers (specifically RootContainer's calc(100dvh - ...)) can
  // subtract it. Without this the banner steals vertical space and the
  // bottom of the nav drawer scrolls off-screen.
  readonly #publishBannerHeight = effect(() => {
    const visible = this.showBanner();
    this.doc.documentElement.style.setProperty(
      '--trial-banner-h',
      visible ? `${TRIAL_BANNER_HEIGHT_PX}px` : '0px',
    );
  });

  // Track the current router URL as a signal so the billingRoute computed
  // re-evaluates whenever the user navigates. Without this, the
  // 'purchase here' link would be locked to whatever URL was active when
  // the component first rendered (often /dashboard before redirectToOrg/
  // redirectToBranch resolves the org+branch IDs).
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  readonly billingRoute = computed<readonly string[]>(() =>
    resolveBillingRoute(
      this.currentUrl(),
      this.authUser.data(),
      this.pinnedBranch.org(),
      this.pinnedBranch.branch(),
    ),
  );

  readonly trialDaysLeft = computed(() =>
    this.subscriptionStore.trialDaysLeft(),
  );
  readonly trialExpired = computed(() => this.subscriptionStore.trialExpired());
  readonly hasActiveSubscription = computed(() =>
    this.subscriptionStore.hasActiveSubscription(),
  );

  readonly isLoggedIn = computed(() => this.auth.loggedIn());

  readonly showBanner = computed(() => {
    // Use config flag to control banner visibility
    if (!this.config.showTrialBanner) return false;

    return (
      this.isLoggedIn() &&
      !this.hasActiveSubscription() &&
      !this.trialExpired() &&
      this.trialDaysLeft() > 0
    );
  });

  @HostBinding('style.display')
  get hostDisplay(): string {
    return this.showBanner() ? '' : 'none';
  }
}
